import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { normalizeString } from '../../utils/stringUtils';
import { getUserInfo } from '../../utils/userUtils';

interface Department {
  id: string;
  name: string;
}

interface Office {
  id: string;
  name: string;
}

interface Selection {
  department: string;
  office: string;
}

export const useOrderDepartment = (isOpen: boolean) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [selection, setSelection] = useState<Selection>({
    department: '',
    office: '',
  });
  const [loading, setLoading] = useState(false);

  const userInfo = getUserInfo();

  // Load offices for a specific department
  const loadOfficesForDepartment = useCallback(
    async (departmentId: string) => {
      try {
        const res = await axios.get(API_ENDPOINTS.departmentOffices(departmentId));
        setOffices(res.data);

        if (res.data.length > 0) {
          // Find user's current office or use first one
          const defaultOffice = userInfo?.office || res.data[0].name;
          const userOffice = res.data.find(
            (office: Office) => normalizeString(office.name) === normalizeString(defaultOffice)
          );

          setSelection(prev => ({
            ...prev,
            office: userOffice ? userOffice.name : res.data[0].name,
          }));
        }
      } catch (error) {
        console.error('Ofisler yüklenemedi:', error);
        setOffices([]);
      }
    },
    [userInfo?.office]
  );

  // Load departments
  useEffect(() => {
    setLoading(true);
    axios
      .get(API_ENDPOINTS.departments)
      .then(res => setDepartments(res.data))
      .catch(error => {
        console.error('Departmanlar yüklenemedi:', error);
        setDepartments([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Set default values when modal opens and departments are loaded
  useEffect(() => {
    if (isOpen && departments.length > 0) {
      const defaultDepartment = userInfo?.management || departments[0].name;

      // Find department with flexible matching
      const dept =
        departments.find(d => normalizeString(d.name) === normalizeString(defaultDepartment)) ||
        departments[0];

      setSelection({
        department: dept.name,
        office: '',
      });

      // Load offices for the selected department
      loadOfficesForDepartment(dept.id);
    }
  }, [isOpen, departments, userInfo?.management, userInfo?.office, loadOfficesForDepartment]);

  // Handle department change
  const handleDepartmentChange = (departmentName: string) => {
    const dept = departments.find(d => d.name === departmentName);
    if (dept) {
      setSelection(prev => ({ ...prev, department: departmentName, office: '' }));
      loadOfficesForDepartment(dept.id);
    }
  };

  // Handle office change
  const handleOfficeChange = (officeName: string) => {
    setSelection(prev => ({ ...prev, office: officeName }));
  };

  return {
    departments,
    offices,
    selection,
    loading,
    handleDepartmentChange,
    handleOfficeChange,
  };
};
